<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAcesso } from 'dashboard/composables/useAcesso';
import { ajustesDaDiferenca, marcadosComAjustes, naoSeDa } from '../catalogo';

import CaixaDeEscopo from './CaixaDeEscopo.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// ACESSO-01 (L9a): os escopos de um módulo, abertos por "Personalizar escopos" ou "Ver escopos". Marcados, os do grupo
// com os ajustes; o que se marca ou desmarca vira o ajuste do módulo, a diferença para o grupo. O rodapé diz, em texto,
// quanto o módulo se afastou do grupo. O escopo que não se dá (R6: não se concede, ou falta a quem edita) fica
// desabilitado, com a nota.
const props = defineProps({
  // Os do módulo, na ordem do catálogo.
  escopos: { type: Array, required: true },
  // O grupo escolhido no módulo (`rotulo`, `escopos`).
  grupo: { type: Object, required: true },
});

const emit = defineEmits(['recolher']);

const ajustes = defineModel('ajustes', { type: Array, required: true });

const { t } = useI18n();
const { pode } = useAcesso();

const doGrupo = computed(() => new Set(props.grupo.escopos));
const marcados = computed(() =>
  marcadosComAjustes(props.grupo.escopos, ajustes.value)
);

const marcar = (nome, marcado) => {
  const novos = new Set(marcados.value);
  if (marcado) novos.add(nome);
  else novos.delete(nome);
  ajustes.value = ajustesDaDiferenca(props.escopos, novos, doGrupo.value);
};

const bloqueado = escopo => naoSeDa(escopo, pode);

const nota = escopo => {
  if (bloqueado(escopo)) return t('PAPEIS.NAO_CONCEDIVEL');
  if (marcados.value.has(escopo.nome) && !doGrupo.value.has(escopo.nome))
    return t('PAPEIS.ESCOPOS.FORA_DO_GRUPO', { grupo: props.grupo.rotulo });
  return '';
};

const resumo = computed(() => {
  const alem = ajustes.value.filter(({ sentido }) => sentido === 'soma').length;
  const menos = ajustes.value.length - alem;
  const frases = {
    alem: t('PAPEIS.ESCOPOS.ALEM', { n: alem }, alem),
    menos: t('PAPEIS.ESCOPOS.A_MENOS', { n: menos }),
  };
  if (alem && menos) return t('PAPEIS.ESCOPOS.ALEM_E_A_MENOS', frases);
  if (alem) return frases.alem;
  if (menos) return frases.menos;
  return t('PAPEIS.ESCOPOS.IGUAL');
});
</script>

<template>
  <div class="pt-1.5 mt-3 border-t border-dashed border-n-strong">
    <CaixaDeEscopo
      v-for="escopo in escopos"
      :key="escopo.nome"
      :escopo="escopo"
      :model-value="marcados.has(escopo.nome)"
      :nota="nota(escopo)"
      :disabled="bloqueado(escopo)"
      @update:model-value="marcado => marcar(escopo.nome, marcado)"
    />
    <div
      class="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 pt-2 text-sm text-n-slate-11"
    >
      <span data-resumo>{{ resumo }}</span>
      <span class="flex items-center gap-4">
        <Button
          v-if="ajustes.length"
          type="button"
          :label="t('PAPEIS.ESCOPOS.VOLTAR_AO_GRUPO')"
          variant="link"
          color="slate"
          size="sm"
          data-voltar-ao-grupo
          @click="ajustes = []"
        />
        <Button
          type="button"
          :label="t('PAPEIS.ESCOPOS.RECOLHER')"
          variant="link"
          color="slate"
          size="sm"
          data-recolher
          @click="emit('recolher')"
        />
      </span>
    </div>
  </div>
</template>
